import { Head, Link, router } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface SubjectItem {
  id: number
  display_name: string
  first_name: string | null
  last_name: string | null
  subject_type: string | null
  city: string | null
  state: string | null
  status: number
}

interface Props {
  case_record: CaseRecord
  subjects: SubjectItem[]
}

const STATUS_LABELS: Record<number, string> = {
  0: "Active",
}

const STATUS_STYLES: Record<number, string> = {
  0: "bg-hpi-dark-green/20 text-hpi-dark-green",
}

function locationString(city: string | null, state: string | null): string {
  return [city, state].filter(Boolean).join(", ")
}

export default function Index({ case_record, subjects }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "Subjects", href: `/cases/${case_record.id}/subjects` },
  ]

  const basePath = `/cases/${case_record.id}/subjects`

  const handleDelete = (id: number) => {
    if (confirm("Are you sure you want to delete this subject?")) {
      router.delete(`${basePath}/${id}`)
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Subjects - ${case_record.case_number}`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            Subjects for {case_record.display_name}
          </h1>
          <Link
            href={`${basePath}/new`}
            className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
          >
            Add Subject
          </Link>
        </div>

        {subjects.length > 0 ? (
          <table className="border-hpi-light-gray w-full border-collapse border text-sm">
            <thead>
              <tr className="bg-hpi-pale-gray">
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Name
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Type
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Location
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Status
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {subjects.map((subject) => (
                <tr key={subject.id} className="hover:bg-hpi-pale-blue">
                  <td className="border-hpi-light-gray border px-4 py-2">
                    <Link
                      href={`${basePath}/${subject.id}`}
                      className="text-hpi-active-blue hover:underline"
                    >
                      {subject.display_name}
                    </Link>
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {subject.subject_type ?? "-"}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {locationString(subject.city, subject.state) || "-"}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    <span
                      className={`rounded px-2 py-0.5 text-xs ${STATUS_STYLES[subject.status] ?? "bg-hpi-pale-gray text-hpi-dark-gray"}`}
                    >
                      {STATUS_LABELS[subject.status] ?? "Unknown"}
                    </span>
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2 whitespace-nowrap">
                    <Link
                      href={`${basePath}/${subject.id}`}
                      className="text-hpi-active-blue mr-2 hover:underline"
                    >
                      View
                    </Link>
                    <Link
                      href={`${basePath}/${subject.id}/edit`}
                      className="text-hpi-active-blue mr-2 hover:underline"
                    >
                      Edit
                    </Link>
                    <button
                      onClick={() => handleDelete(subject.id)}
                      className="text-hpi-red hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-hpi-med-gray">No subjects found.</p>
        )}

        <div className="mt-4">
          <Link
            href={`/cases/${case_record.id}`}
            className="text-hpi-active-blue hover:underline"
          >
            Back to Case
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
